import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import recruiterApiService from "../../../core/recruiterApiService";
import Pagination from "../../../shared/Pagination";

const EMPTY_DETAILS = { currentCtc: "", expectedCtc: "", fixedPay: "", variablePay: "", bonus: "", compensationComments: "", agreedCtc: "" };

// Agreed CTC is not manually entered - it's always the sum of Fixed Pay + Variable Pay.
const computeAgreedCtc = (fixedPay, variablePay) => {
  if (fixedPay === "" && variablePay === "") return "";
  return String((Number(fixedPay) || 0) + (Number(variablePay) || 0));
};

/** Compensation Management (Section 15 of the requirements doc, renamed from "Compensation Pool"). */
const CompensationPoolTab = ({ positionId, isActive }) => {
  const [rows, setRows] = useState([]);
  const [details, setDetails] = useState({});
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [searchText, setSearchText] = useState("");

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await recruiterApiService.searchCompensationPool({ positionId, page, size, searchText });
      const content = res.data.data.content || [];
      setRows(content);
      const map = {};
      content.forEach((c) => {
        const fixedPay = c.fixedPay ?? "";
        const variablePay = c.variablePay ?? "";
        map[c.id] = {
          currentCtc: c.currentCtc ?? "",
          expectedCtc: c.expectedCtc ?? "",
          fixedPay,
          variablePay,
          bonus: c.bonus ?? "",
          compensationComments: c.compensationComments ?? "",
          agreedCtc: computeAgreedCtc(fixedPay, variablePay),
        };
      });
      setDetails(map);
      setTotalPages(res.data.data.totalPages || 0);
    } catch (e) {
      toast.error("Failed to load Compensation Management");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    load();
    setSelected([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, page, size]);

  // Clear the selection and search text when navigating away to another tab - since this tab
  // now stays mounted (to avoid a reload flicker on revisit), they would otherwise persist.
  // On the other hand, silently re-fetch (no loading flash) when revisiting - a candidate may
  // have just been moved in here from another tab (e.g. Interview Pool) while we were away.
  const prevIsActiveRef = useRef(isActive);
  useEffect(() => {
    if (!isActive) {
      setSelected([]);
      setSearchText("");
    } else if (!prevIsActiveRef.current) {
      load(true);
    }
    prevIsActiveRef.current = isActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Only reload on a genuine searchText change, not on mount (compares actual values rather
  // than a "have I run before" flag, so it stays correct under StrictMode's double-invoke too).
  const prevSearchTextRef = useRef(searchText);
  useEffect(() => {
    if (prevSearchTextRef.current === searchText) return;
    prevSearchTextRef.current = searchText;
    const timeout = setTimeout(() => {
      setPage(0);
      load();
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const updateField = (id, field, value) => {
    setDetails((prev) => {
      const current = { ...(prev[id] || EMPTY_DETAILS), [field]: value };
      if (field === "fixedPay" || field === "variablePay") {
        current.agreedCtc = computeAgreedCtc(current.fixedPay, current.variablePay);
      }
      return { ...prev, [id]: current };
    });
  };

  const handleSaveDetails = async (id) => {
    setSavingId(id);
    try {
      const d = details[id] || EMPTY_DETAILS;
      await recruiterApiService.updateCompensationDetails(id, {
        currentCtc: d.currentCtc || null,
        expectedCtc: d.expectedCtc || null,
        fixedPay: d.fixedPay || null,
        variablePay: d.variablePay || null,
        bonus: d.bonus || null,
        compensationComments: d.compensationComments || null,
        agreedCtc: d.agreedCtc || null,
      });
      toast.success("Compensation details saved successfully");
      await load(true);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save compensation details");
    } finally {
      setSavingId(null);
    }
  };

  const canSelectForOffer = (c) => c.status === "COMPENSATION_SUBMITTED" && !!details[c.id]?.agreedCtc;

  const toggleSelect = (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row || row.status !== "COMPENSATION_SUBMITTED") {
      toast.error("Save compensation details first — status must be Compensation Submitted");
      return;
    }
    if (!details[id]?.agreedCtc) {
      toast.error("Enter and save Agreed CTC before selecting this candidate");
      return;
    }
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleMoveToOffer = async () => {
    try {
      await recruiterApiService.moveToOffer(selected);
      toast.success("Candidate(s) moved to Offer Pool successfully");
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to move candidates");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <div className="search-boxpost">
            <i className="bi bi-search" />
            <input className="form-control form-control-sm" placeholder="Search candidates..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          </div>
          {selected.length > 0 && (
            <span className="badge rounded-pill text-bg-light border text-app-primary fs-13">{selected.length} Candidates Selected</span>
          )}
        </div>
        {selected.length > 0 && (
          <button className="btn btn-blue-dark" onClick={handleMoveToOffer}>
            Move to Offer Pool ({selected.length})
          </button>
        )}
      </div>

      {loading ? <div>Loading...</div> : (
        <table className="table table-hover align-middle">
          <thead>
            <tr className="text-muted fs-13">
              <th></th>
              <th>Candidate</th>
              <th>Score</th>
              <th>Current CTC</th>
              <th>Expected CTC</th>
              <th>Fixed Pay</th>
              <th>Variable Pay</th>
              <th>Bonus</th>
              <th>Comments</th>
              <th>Agreed CTC (<small className="text-muted fw-normal">Excludes Bonus</small>)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const d = details[c.id] || EMPTY_DETAILS;
              return (
                <tr key={c.id}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selected.includes(c.id)}
                      disabled={!canSelectForOffer(c)}
                      onChange={() => toggleSelect(c.id)}
                      title={canSelectForOffer(c) ? "Select for Offer Pool" : "Save compensation details first"}
                    />
                  </td>
                  <td>{c.name}</td>
                  <td>{c.finalScore ?? "-"}</td>
                  <td><input type="number" className="form-control form-control-sm" style={{ width: 110 }} value={d.currentCtc} onChange={(e) => updateField(c.id, "currentCtc", e.target.value)} /></td>
                  <td><input type="number" className="form-control form-control-sm" style={{ width: 110 }} value={d.expectedCtc} onChange={(e) => updateField(c.id, "expectedCtc", e.target.value)} /></td>
                  <td><input type="number" className="form-control form-control-sm" style={{ width: 100 }} value={d.fixedPay} onChange={(e) => updateField(c.id, "fixedPay", e.target.value)} /></td>
                  <td><input type="number" className="form-control form-control-sm" style={{ width: 100 }} value={d.variablePay} onChange={(e) => updateField(c.id, "variablePay", e.target.value)} /></td>
                  <td><input type="number" className="form-control form-control-sm" style={{ width: 90 }} value={d.bonus} onChange={(e) => updateField(c.id, "bonus", e.target.value)} /></td>
                  <td><input className="form-control form-control-sm" style={{ width: 140 }} value={d.compensationComments} onChange={(e) => updateField(c.id, "compensationComments", e.target.value)} /></td>
                  <td><input type="number" className="form-control form-control-sm" style={{ width: 110 }} value={d.agreedCtc} disabled /></td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary" disabled={savingId === c.id} onClick={() => handleSaveDetails(c.id)}>
                      {savingId === c.id ? "Saving..." : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="text-center text-muted py-4">No candidates in Compensation Management.</td></tr>
            )}
          </tbody>
        </table>
      )}

      <Pagination page={page} totalPages={totalPages} onChange={setPage} size={size} onSizeChange={(s) => { setSize(s); setPage(0); }} />
    </div>
  );
};

export default CompensationPoolTab;
